import React, { useEffect, useImperativeHandle } from "react";

import * as CONSTANT from "../../utils/constant";
import CardLayout from "react-lib/apps/common/CardLayout";
import SubFormViewer from "react-lib/apps/PTM/SubFormViewer";
import { Label, SemanticCOLORS } from "semantic-ui-react";
import { useIntl } from "react-intl";

interface CardPatientAssessmentProps {
  onClose?: () => void;
  controller?: Record<string, any>;
  encounterId?: string | number;
  showSummary?: boolean;
}
const DEFAULT_PROPS = {
  onClose: () => {},
  controller: {},
  encounterId: null,
  showSummary: false,
};

type encounterIdType = {
  encounterId?: string | number;
};

const CardPatientAssessment = React.forwardRef<any, CardPatientAssessmentProps>(
  (propsParam, ref) => {
    const props = {
      ...DEFAULT_PROPS,
      ...Object.fromEntries(Object.entries(propsParam).filter(([_, value]) => value !== undefined)),
    };
    const [allowedActions, setAllowedActions] = React.useState<any[]>([]);
    const [patientAssessmentData, setPatientAssessmentData] = React.useState<patientAssessmentData>(
      {}
    );
    const [subFormViewerData, setSubFormViewerData] = React.useState({});
    const [faChoices, setFAChoices] = React.useState<any[]>([]);
    const [saChoices, setSAChoices] = React.useState<any[]>([]);
    const [status, setStatus] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [formVersion, setFormVersion] = React.useState("");

    useEffect(() => {
      getFunctionalAssessmentChoices();
      getSpiritualAssessmentChoices();
    }, []);

    useImperativeHandle(ref, () => ({
      refresh: ({ encounterId }: encounterIdType = {}) => {
        refresh({ encounterId });
      },
    }));

    useEffect(() => {
      console.log("CardPatientAssessment useEffect patientAssessmentData: ", patientAssessmentData);
      formatFormViewData();
    }, [patientAssessmentData]);

    useEffect(() => {
      refresh({ encounterId: props.encounterId || "" });
    }, [props.encounterId]);

    const formatFormViewData = () => {
      let newObj = { ...patientAssessmentData };

      const newData = {
        ...newObj,
        ...(newObj.arrive_status && { arrive_status: ARRIVED_BY[newObj.arrive_status] }),
        ...(newObj.translator && { translator: newObj.translator ? "ใช้" : "ไม่ใช้" }),
        ...(newObj.triage_level && { triage_level: CONSTANT.TRIAGE_LEVEL[newObj.triage_level] }),
        ...(newObj.psychological && {
          psychological:
            formVersion === "0.1"
              ? newObj.psychological
                ? "Abnormal"
                : "Normal"
              : newObj.psychological
              ? "แสดงออกทางอารมณ์และพฤติกรรมผิดปกติ"
              : "แสดงออกทางอารมณ์และพฤติกรรมปกติ",
        }),
        ...(newObj.phyco_nurse_note && {
          phyco_nurse_note: PSYCHO_NURSE_NOTE[newObj.phyco_nurse_note],
        }),
        ...(newObj.spiritual && { spiritual: newObj.spiritual ? "YES" : "NO" }),
        ...(newObj.suspected && {
          suspected:
            formVersion === "0.1"
              ? newObj.suspected
                ? "พบร่องรอยการถูกทำร้าย"
                : "ไม่พบร่องรอยการถูกทำร้าย"
              : newObj.suspected
              ? "แสดงออกทางอารมณ์และพฤติกรรมผิดปกติ"
              : "แสดงออกทางอารมณ์และพฤติกรรมปกติ",
        }),
        ...(newObj.sus_nurse_note && { sus_nurse_note: SUS_NURSE_NOTE[newObj.sus_nurse_note] }),
        ...(newObj.convulsion && { convulsion: CONSTANT.YES_NO[newObj.convulsion] }),
        ...(newObj.mdr_triage && { mdr_triage: CONSTANT.YES_NO[newObj.mdr_triage] }),
        ...(newObj.mdr && { mdr: CONSTANT.YES_NO[newObj.mdr] }),
        ...(newObj.alcohol && { alcohol: CONSTANT.ALCOHOL[newObj.alcohol] }),
        ...(newObj.tobacco && { tobacco: CONSTANT.TOBACCO[newObj.tobacco] }),
        ...(newObj.narcotic && { narcotic: CONSTANT.NARCOTIC[newObj.narcotic] }),
      };

      let faIndex = faChoices.findIndex((item) => item.id == newObj.functional);

      if (faIndex > -1) {
        newData.functional = faChoices[faIndex].name;
      }

      setSubFormViewerData(newData);
    };

    const refresh = ({ encounterId }: encounterIdType = {}) => {
      getFormDataLatest({ encounterId });
    };

    const getFormDataLatest = async ({ encounterId }: encounterIdType) => {
      setIsLoading(true);

      // checkForm
      const [formAssessmentV1, formAssessmentV2] = await Promise.all([
        props.controller.getFormDataLatest({
          encounterId: encounterId ? encounterId : props.patientData.ENCOUNTER.encounter_id,
          formCode: "CardPatientAssessment",
          formVersion: "0.1",
        }),
        props.controller.getFormDataLatest({
          encounterId: encounterId ? encounterId : props.patientData.ENCOUNTER.encounter_id,
          formCode: "CardPatientAssessment",
          formVersion: "0.2",
        }),
      ]);

      let patientDataV2;

      if (formAssessmentV2?.[0]) {
        let assessmentData = formAssessmentV2?.[0]?.data;

        let newPrecaution = "";
        (assessmentData?.precaution || []).forEach((item: PrecautionType) => {
          if (newPrecaution === "") {
            newPrecaution = `${PRECAUTION[item]}`;
          } else {
            newPrecaution = `${newPrecaution}, ${PRECAUTION[item]}`;
          }
        });

        patientDataV2 = {
          ...assessmentData,
          phyco_nurse_note: assessmentData?.phyco_nurse_noteV2,
          psychological: assessmentData?.psychologicalV2,
          precaution: newPrecaution,
        };

        setFormVersion("0.2");
      } else {
        setFormVersion("0.1");
      }

      let data = formAssessmentV1?.[0]
        ? formAssessmentV1?.[0]
        : {
            ...formAssessmentV2?.[0],
            data: patientDataV2,
          };

      // const [data, error] = await props.controller.getFormDataLatest({
      //   encounterId: encounterId
      //     ? encounterId
      //     : props.patientData.ENCOUNTER.encounter_id,
      //   formCode: "CardPatientAssessment",
      //   formVersion: "0.1",
      // });

      console.log("CardPatientAssessment getFormDataLatest data: ", data);
      if (data) {
        setStatus(data.status);
        setAllowedActions(data.allowed_actions);
        setPatientAssessmentData({
          ...data.data,

          // "date",
          // "user_fullname",

          created: data?.created,
          created_utc: data?.created_utc,

          date: data?.edited,
          edited_utc: data?.edited_utc,

          edit_user: data?.edit_user,
          user_fullname: data?.edit_user_name,
        });
      }
      setIsLoading(false);
    };

    const getFunctionalAssessmentChoices = async () => {
      const [data, error] = await props.controller.getClinicalTerm({
        type: "FA",
      });
      setFAChoices(data);
    };

    const getSpiritualAssessmentChoices = async () => {
      const [data, error] = await props.controller.getClinicalTerm({
        type: "SA",
      });
      setSAChoices(data);
    };

    let statusColor: SemanticCOLORS = "black";
    if (status === CONSTANT.FORM_DATA_STATUS.DRAFT) {
      statusColor = "yellow";
    } else if (status === CONSTANT.FORM_DATA_STATUS.EDITED) {
      statusColor = "olive";
    } else if (status === CONSTANT.FORM_DATA_STATUS.CONFIRMED) {
      statusColor = "green";
    } else if (status === CONSTANT.FORM_DATA_STATUS.CANCELLED) {
      statusColor = "red";
    }

    let labelStatus = <Label color={statusColor}>{status}</Label>;

    return (
      <CardLayout
        loading={isLoading}
        titleText="แบบประเมินแรกรับ"
        headerColor="grey"
        onClose={props.onClose}
        titleContent={labelStatus}
      >
        {props.showSummary && (
          <SubFormViewer
            fieldList={rmdFieldList}
            fieldMap={rmdFieldMap}
            data={[subFormViewerData]}
            languageUX={props.languageUX}
          />
        )}
      </CardLayout>
    );
  }
);

const ARRIVED_BY = {
  WLK: "เดิน",
  WCH: "รถนั่ง",
  CRT: "รถนอน",
  ATH: "อื่นๆ พิมพ์...",
} as const;

const TRANSLATOR = {
  true: "ใช้",
  false: "ไม่ใช้",
};

const PSYCHOLOGICAL_ASSESSMENT = {
  true: "Abnormal",
  false: "Normal",
};

const PSYCHO_NURSE_NOTE = {
  NON: "No Intervention Require",
  REP: "Action Plan Require",
  ND: "Notify Doctor",
};

const SPIRITUAL = {
  true: "YES",
  false: "NO",
};

const SUSPECTED = {
  true: "พบร่องรอยการถูกทำร้าย",
  false: "ไม่พบร่องรอยการถูกทำร้าย",
};

const SUS_NURSE_NOTE = {
  NON: "ไม่จำเป็น",
  REP: "รายงานแพทย์",
  OTN: "อื่นๆ",
};

const rmdFieldList = [
  "date",
  "user_fullname",
  "arrive_status",
  "translator",
  "chief_complaint",
  "triage_level",
  "functional",
  "psychological",
  "phyco_nurse_note",
  "spiritual",
  "suspected",
  "sus_nurse_note",
  "convulsion",
  "mdr_triage",
  "mdr",
  "precaution",
  "alcohol",
  "tobacco",
  "narcotic",
  "menstruation",
  "lmp",
];

const SUSPECTED_V2 = {
  NON: "ไม่พบร่องรอยผิดปกติจากการประเมินโดยการสังเกต",
  SUS1: "พบร่องรอยบาดแผล รอยฟกช้ำ ไม่สัมพันธ์กับการเจ็บป่วย รายงานแพทย์",
  SUS2: "พบร่องรอยบาดแผล รอยฟกช้ำ มีอาการหวาดกลัว ซึมเศร้า ถามไม่ตอบหรือเหม่อลอย รายงานแพทย์",
};

const PSYCHOLOGICAL_ASSESSMENT_V2 = {
  true: "แสดงออกทางอารมณ์และพฤติกรรมผิดปกติ",
  false: "แสดงออกทางอารมณ์และพฤติกรรมปกติ",
};

const PRECAUTION = {
  SP: "Standard Precaution",
  AP: "Airborned Precaution",
  DP: "Droplet Precaution",
  CP: "Contact Precaution",
};

const rmdFieldMap = {
  date: "Date",
  user_fullname: "ผู้บันทึก/แก้ไข",
  arrive_status: "Arrived by",
  translator: "ใช้ล่าม",
  chief_complaint: "Chief Complaint",
  triage_level: "ระดับการคัดกรอง",
  functional: "Functional Assessment",
  psychological: "Psychological Assessment",
  phyco_nurse_note: "การให้การพยาบาลผู้ป่วย",
  spiritual: "Spiritual/Cultural Assessment",
  suspected: "Suspected abuse or neglect",
  sus_nurse_note: "การให้การพยาบาลผู้ป่วย",
  convulsion: "History of Febrile convulsion",
  mdr_triage: "เสี่ยงต่อการติดเชื้อ",
  mdr: "MDR",
  precaution: "Isolate Precaution",
  alcohol: "ประวัติการดื่มสุรา",
  tobacco: "ประวัติการสูบบุหรี่",
  narcotic: "ประวัติการใช้สารเสพติด",
  menstruation: "ประจำเดือน",
  lmp: "LMP",
};

export default React.memo(CardPatientAssessment);
CardPatientAssessment.displayName = "CardPatientAssessment";

type KeyOf<T> = keyof T;

type ArrivedByType = KeyOf<typeof ARRIVED_BY>;
type TriageLevelType = KeyOf<typeof CONSTANT.TRIAGE_LEVEL>;
type PsychoNurseNoteType = KeyOf<typeof PSYCHO_NURSE_NOTE>;
type SuspectedV2Type = KeyOf<typeof SUSPECTED_V2>;
type SusNurseNoteType = KeyOf<typeof SUS_NURSE_NOTE>;
type YesNoType = KeyOf<typeof CONSTANT.YES_NO>;
type AlcoholType = KeyOf<typeof CONSTANT.ALCOHOL>;
type TobaccoType = KeyOf<typeof CONSTANT.TOBACCO>;
type NarcoticType = KeyOf<typeof CONSTANT.NARCOTIC>;
type PrecautionType = KeyOf<typeof PRECAUTION>;

type patientAssessmentData = {
  alcohol?: AlcoholType;
  alcohol_specific?: string;
  tobacco?: TobaccoType;
  tobacco_specific?: string;
  narcotic?: NarcoticType;
  narcotic_specific?: string;
  psychological?: boolean;
  psychological_specific?: string;
  psychologicalV2?: boolean;
  psychological_select?: string;
  psychological_detail?: number[];
  psychological_text?: string;
  phyco_nurse_note?: PsychoNurseNoteType;
  phyco_nurse_note_specific?: string;
  phyco_nurse_noteV2?: string;
  phyco_nurse_note_specific_detail?: string;
  spiritual?: boolean;
  spiritual_detail?: string;
  menstruation?: string;
  lmp?: string;
  menstruationOther?: string;
  translator?: boolean;
  translator_specific?: string;
  functional?: string;
  functional_assm_other?: string;
  suspected?: boolean | SuspectedV2Type;
  suspected_specific?: string;
  sus_nurse_note?: SusNurseNoteType;
  sus_nurse_note_specific?: string;
  precaution?: string[];
  convulsion?: YesNoType;
  mdr_triage?: YesNoType;
  mdr?: YesNoType;
  arrive_status?: ArrivedByType;
  chief_complaint?: string;
  triage_level?: TriageLevelType;
};
